import { FileTypes, SupportedFileWithMetadata } from 'APP/Converse/FilePreview/types'
import { FileUploadStatusEnum } from 'APP/Store/History/types'

type UploadPayload = {
  fileUri: string
  fileType: string
  fileName: string
  file?: unknown
}

export type QueueFileInput = SupportedFileWithMetadata & { file?: unknown }

export type WebQueueFileInput = {
  file: File
  uri: string
  name: string
  size: number
  type: FileTypes
  fileType?: string
}

export type FileTileMetadata = {
  originalIndex: number
  isPlaceholder: boolean
  isProcessingFile: boolean
  marginRight: number
}

export const isPendingFile = (file: SupportedFileWithMetadata) =>
  file.uploadStatus === FileUploadStatusEnum.UPLOADING ||
  file.uploadStatus === FileUploadStatusEnum.REMOVING_FILE ||
  (!file.fileId && file.uploadStatus !== FileUploadStatusEnum.FAILED)

export const updateFileStatus = (
  file: SupportedFileWithMetadata,
  status?: FileUploadStatusEnum | null,
  fileId?: string | null,
  fileInfo?: { id: string } | null,
  error?: string | null
): SupportedFileWithMetadata => {
  if (!isPendingFile(file)) {
    return file
  }

  switch (status) {
    case FileUploadStatusEnum.UPLOADING:
      return { ...file, uploadStatus: FileUploadStatusEnum.UPLOADING, uploadError: null }
    case FileUploadStatusEnum.UPLOADED:
      return {
        ...file,
        uploadStatus: FileUploadStatusEnum.UPLOADED,
        fileId: fileId ?? fileInfo?.id ?? file.fileId,
        uploadError: null,
      }
    case FileUploadStatusEnum.FAILED:
      return {
        ...file,
        uploadStatus: FileUploadStatusEnum.FAILED,
        uploadError: error ?? 'Upload failed',
      }
    case FileUploadStatusEnum.REMOVING_FILE:
      return { ...file, uploadStatus: FileUploadStatusEnum.REMOVING_FILE }
    default:
      return file
  }
}

export const buildUploadPayload = (file: QueueFileInput, fileType: string): UploadPayload => {
  return {
    fileUri: file.uri,
    fileType,
    fileName: file.name,
    ...(file.file ? { file: file.file } : {}),
  }
}

export const canQueueFile = (
  channelId: string | null,
  currentFiles: SupportedFileWithMetadata[],
  maxFiles: number,
  incomingFile: QueueFileInput
): boolean => {
  if (!channelId) return false
  if (currentFiles.length === maxFiles) return false
  if (currentFiles.some((file) => file.uri === incomingFile.uri)) return false
  return true
}

export const isDuplicateSelection = (
  currentFiles: SupportedFileWithMetadata[],
  incomingFile: QueueFileInput
): boolean => {
  return currentFiles.some((file) => {
    if (file?.uri && incomingFile?.uri && file.uri === incomingFile.uri) {
      return true
    }

    if (!file?.name || !incomingFile?.name) {
      return false
    }

    const existingSize = file?.size
    const incomingSize = incomingFile?.size

    if (typeof existingSize === 'number' && typeof incomingSize === 'number') {
      return file.name === incomingFile.name && existingSize === incomingSize
    }

    return false
  })
}

export const createFileWithMetadata = (
  incomingFile: QueueFileInput
): SupportedFileWithMetadata & { fileType: string } => {
  const fallbackType = incomingFile.type === FileTypes.PDF ? 'application/pdf' : 'image/jpeg'
  const fileType = incomingFile.fileType ?? fallbackType

  return {
    ...incomingFile,
    fileType,
    uploadStatus: FileUploadStatusEnum.UPLOADING,
    uploadError: null,
  }
}

/**
 * Web-specific file queue validation
 */

export const canQueueFileWeb = (
  currentFiles: (SupportedFileWithMetadata & { rawFile?: File })[],
  maxFiles: number,
  incomingFile: File
): boolean => {
  if (currentFiles.length === maxFiles) return false

  const isDuplicate = currentFiles.some(
    (file) =>
      file.rawFile &&
      file.rawFile.name === incomingFile.name &&
      file.rawFile.size === incomingFile.size
  )

  return !isDuplicate
}

export const chunkFilesByRow = (
  files: (SupportedFileWithMetadata | null)[],
  perRow: number,
  minItemsPerRow = 1
): (SupportedFileWithMetadata | null)[][] => {
  if (!files.length) return []

  const chunkSize = Math.max(perRow, minItemsPerRow)
  const chunks: (SupportedFileWithMetadata | null)[][] = []

  files.forEach((file, index) => {
    if (index % chunkSize === 0) {
      chunks.push([file])
    } else {
      chunks[chunks.length - 1]?.push(file)
    }
  })

  return chunks
}

export const buildFileTileMetadata = (
  file: SupportedFileWithMetadata | null,
  rowIndex: number,
  indexWithinRow: number,
  itemsPerRow: number,
  isLastInRow: boolean,
  shouldRenderInlineUpload: boolean,
  spacing: number
): FileTileMetadata => {
  const originalIndex = rowIndex * itemsPerRow + indexWithinRow
  const isPlaceholder = file === null
  const isProcessingFile =
    file?.uploadStatus === FileUploadStatusEnum.UPLOADING ||
    file?.uploadStatus === FileUploadStatusEnum.REMOVING_FILE
  const marginRight = isLastInRow && shouldRenderInlineUpload ? spacing : isLastInRow ? 0 : spacing

  return {
    originalIndex,
    isPlaceholder,
    isProcessingFile,
    marginRight,
  }
}
